import { codeInputCommonAttrs } from '../../../common/$models'
import { ModelDef } from '../../../common/$models/ModelDef'
import {
  ModelCoreAnotherDataSourceDialect,
  sourceTypeSubSelectionsBySourceTypes,
} from './model_core_another_data_source_dialects'

const selectQueryExample =
  'SELECT productDetails.*, products.* FROM productDetails LEFT JOIN products AS p ON p.id = productDetails.productId'

export type ModelCoreAnotherDataSourceConnectionDefinition = {
  enabled: boolean
  name: string
  useExternalDialect: boolean
  dialect?: ModelCoreAnotherDataSourceDialect | string
  useRawDbQuery: boolean
  selectQuery: string
  /** スキーマ名（任意）。Geonome の org スキーマ等。空なら接続先デフォルト / tableName のみ */
  schemaName?: string
  tableName: string
  primaryKeys: string[]
  fields: string[]
  /**
   * true かつ useRawDbQuery=false かつ外部 dialect 利用時のみ
   * ModelDef 経由の bulkUpsert / bulkDelete を許可する（デフォルト false・オプトイン）
   */
  enableWrite?: boolean
  beforeFindQueryTransformFunction: string
  afterFindFunction: string
}

export const core_another_data_source_connection_definitions: ModelDef = {
  tableName: 'core_another_data_source_connection_definitions',
  tableLabel: 'データソース定義',
  tableComment:
    'SQLを利用した複雑なクエリによるデータモデルの定義や、外部データソースの接続情報を定義可能。接続先は別テーブル core_another_data_source_dialects に保持されており、Relationしている。',
  primaryKeyColType: 'UUID',
  modelType: 'admin',
  columns: {
    name: {
      beforeComponent:
        '<div class="alert alert-info">SQLを利用した複雑なクエリや、外部SQL系DB, および API接続などを利用したモデル定義のデータソースの定義が可能です。</div>',
      type: 'STRING',
      label: 'データソース名',
      validate: { notEmpty: true },
      width: { xs: 48 },
      unique: true,
      afterComponent: {
        template: `<div class="small mt-1">
        - モデル定義のデータソースとして設定する場合: <code>anotherDataSourceName: '{{ record.name }}'</code> を設定します。<br/>
        - 読取 API: <code>/core/anotherDatasourceFind</code><br/>
        - 書込（enableWrite 時）: <code>/core/anotherDatasourceBulkUpsert</code> / <code>/core/anotherDatasourceBulkDelete</code><br/>
        例:
<pre class="bg-light p-1">const result = await $core.$d.transport.post('/core/anotherDatasourceFind', {
  // ...query,
  dataSourceName: '{{ record.name }}',
})
console.log(result.data) // 結果の配列
</pre></div>`,
        computed: {
          record() {
            return this.$parent.record
          },
        },
      },
      listItemAttrs: { class: { 'no-ellipsis': true } },
    },
    desc: {
      type: 'TEXT',
      label: '説明',
      width: { xs: 48 },
      inputAttrs: {
        rows: 5,
      },
      listItemAttrs: {
        class: { 'no-ellipsis': true, 'whitespace-wrap': true },
        style: { minWidth: '400px', maxWidth: '100%' },
      },
    },
    enabled: {
      type: 'BOOLEAN',
      label: '有効状態',
      width: {
        md: 6,
      },
      defaultValue: true,
    },
    useExternalDialect: {
      type: 'BOOLEAN',
      label: '外部ソース接続先を利用',
      defaultValue: false,
      inputHelpText:
        '外部ソース接続先を利用しない場合は、メインDBに対してクエリを実行します。',
      width: {
        md: 18,
      },
    },
    dialect: {
      enableIf: (row) => row?.useExternalDialect,
      type: 'RELATIONSHIP_MANY_TO_ONE',
      label: '外部ソース接続先',
      relationshipManyToOne: {
        collectionName: 'core_another_data_source_dialects',
        labelFormatter: (record) => {
          return `"${record?.name}" - ${
            sourceTypeSubSelectionsBySourceTypes[record?.connectionType]?.label ||
            record?.connectionType
          }:${
            sourceTypeSubSelectionsBySourceTypes[record?.connectionType]
              ?.subSourceTypes?.[record?.subSourceType]?.label || ''
          }`
        },
      },
      width: { md: 24 },
      validate: { notEmpty: true },
    },
    useRawDbQuery: {
      type: 'BOOLEAN',
      label: 'データソースに SQL表現を使用する',
      inputHelpText:
        'SQL表現を使用して、JOINやサブクエリを利用した複雑なデータソースを定義可能です。',
      defaultValue: false,
      width: { md: 48 },
    },
    schemaName: {
      type: 'STRING',
      label: 'スキーマ名',
      enableIf: (row) => !row.useRawDbQuery,
      width: { md: 16 },
      inputHelpText:
        '任意。Geonome の個社スキーマ（例: kantei, base）など。空欄の場合は接続先のデフォルトスキーマが使われます。',
      afterComponent: {
        template: `
<div class="small mt-1" v-if="showHint">
  <span v-if="loading">接続先のデフォルトスキーマを取得中...</span>
  <span v-else-if="defaultSchema">
    接続先のデフォルトスキーマ: <code class="bg-light px-1">{{ defaultSchema }}</code>
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary ms-1"
      @click="applyDefault"
      v-if="!record.schemaName"
    >この値を入力</button>
  </span>
  <span v-else-if="error" class="text-muted">{{ error }}</span>
</div>`,
        data() {
          return {
            defaultSchema: '',
            loading: false,
            error: '',
            lastDialectId: null as string | null,
          }
        },
        computed: {
          record() {
            return this.$parent.record
          },
          showHint() {
            return !!this.record?.useExternalDialect && !!this.record?.dialect
          },
          dialectId() {
            const d = this.record?.dialect
            return typeof d === 'string' ? d : d?.id || null
          },
        },
        watch: {
          dialectId: {
            immediate: true,
            handler(id: string | null) {
              this.fetchDefaultSchema(id)
            },
          },
        },
        methods: {
          async fetchDefaultSchema(dialectId: string | null) {
            if (!dialectId || !this.record?.useExternalDialect) {
              this.defaultSchema = ''
              this.error = ''
              return
            }
            if (this.lastDialectId === dialectId && this.defaultSchema) {
              return
            }
            this.loading = true
            this.error = ''
            try {
              // @ts-ignore
              const res = await $core.$d.transport.post(
                '/core/anotherDatasourceDefaultSchema',
                { dialectId },
              )
              this.defaultSchema = res?.data?.defaultSchema || ''
              this.lastDialectId = dialectId
            } catch (e) {
              this.defaultSchema = ''
              this.error = 'デフォルトスキーマを取得できませんでした'
              console.warn('[schemaName] failed to fetch default schema', e)
            } finally {
              this.loading = false
            }
          },
          applyDefault() {
            if (this.defaultSchema && this.record) {
              this.record.schemaName = this.defaultSchema
            }
          },
        },
      },
    },
    tableName: {
      type: 'STRING',
      label: 'テーブル名',
      enableIf: (row) => !row.useRawDbQuery,
      width: { md: 16 },
      validate: { notEmpty: true },
      inputHelpText:
        'テーブル名のみを指定してください（スキーマは「スキーマ名」欄）。後方互換のため schema.table 形式も解釈されます。',
    },
    primaryKeys: {
      type: 'MULTISELECT',
      label: '主キー',
      enableIf: (row) => !row.useRawDbQuery,
      width: { md: 16 },
      inputHelpText: '複数入力でマルチプライマリキーに対応可能です。',
      validate: { notEmpty: true },
    },
    fields: {
      type: 'MULTISELECT',
      label: 'SELECT対象 フィールド',
      enableIf: (row) => !row.useRawDbQuery,
      width: { md: 16 },
      inputHelpText: '空の場合は全フィールド選択 (`SELECT *`) を実行します',
    },
    enableWrite: {
      type: 'BOOLEAN',
      label: '書込を許可（新規・更新・削除）',
      defaultValue: false,
      enableIf: (row) => !row.useRawDbQuery && row.useExternalDialect,
      width: { md: 24 },
      inputHelpText:
        '有効時のみ、ModelDef（anotherDataSourceName）から通常の新規作成・更新・削除が可能になります。SQL表現モード・メインDB接続では利用できません。デフォルトは無効（読取専用）です。',
    },
    selectQuery: {
      type: 'TEXT',
      label: 'SELECT Query',
      enableIf: (row) => row.useRawDbQuery,
      inputAttrs: {
        ...codeInputCommonAttrs,
        language: 'sql',
      },
      width: {
        md: 48,
      },
      afterComponent: `<div class="small" style="line-height: 1.6em">SELECT SQL文を入力します。例: <code class="bg-light p-1">${selectQueryExample}</code><br/>※ Model定義のデータソースとして利用する場合の制約: プライマリキーが1つのフィールドになるように設定し、このデータソースを利用するモデル定義でも、プライマリキーカラムを同名で正しく設定してください。 (複合主キーの例: <code class="bg-light p-1">SELECT CONCAT(code, sub_code) AS id, ...</code>) </div>`,
      validate: { notEmpty: true },
    },
    beforeFindQueryTransformFunction: {
      type: 'TEXT',
      inputAttrs: {
        ...codeInputCommonAttrs,
      },
      width: {
        md: 48,
      },
    },
    afterFindFunction: {
      type: 'TEXT',
      inputAttrs: {
        ...codeInputCommonAttrs,
      },
      width: {
        md: 48,
      },
    },
  },
}
